import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save, Send, ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import newsletterService from '../../services/newsletterService';
import RichTextEditor from '../../components/ui/RichTextEditor';

const CreateNewsletterPage = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    status: 'draft'
  });

  const [charCounts, setCharCounts] = useState({
    title: 0,
    excerpt: 0,
    content: 0
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'content') {
      // For rich text editor, value is already HTML
      setCharCounts(prev => ({ ...prev, [field]: value?.replace(/<[^>]*>/g, '')?.length || 0 }));
    } else {
      setCharCounts(prev => ({ ...prev, [field]: value?.length || 0 }));
    }
    setError('');
  };

  const handleImageInsert = (imageHtml) => {
    // Insert image HTML directly into the rich text editor content
    // Append at the end since React Quill doesn't expose cursor position easily
    const newContent = (formData?.content || '') + imageHtml;
    handleInputChange('content', newContent);
  };

  const generateSlug = (title) => {
    return title
      ?.toLowerCase()
      ?.trim()
      ?.replace(/[^a-z0-9\s-]/g, '')
      ?.replace(/\s+/g, '-')
      ?.replace(/-+/g, '-')
      ?.substring(0, 100) || '';
  };

  const validateForm = () => {
    if (!formData?.title?.trim()) {
      setError('Title is required');
      return false;
    }
    if (formData?.title?.length < 5) {
      setError('Title must be at least 5 characters');
      return false;
    }
    if (!formData?.excerpt?.trim()) {
      setError('Excerpt is required');
      return false;
    }
    if (formData?.excerpt?.length < 20) {
      setError('Excerpt must be at least 20 characters');
      return false;
    }
    if (!formData?.content?.trim()) {
      setError('Content is required');
      return false;
    }
    if (formData?.content?.length < 50) {
      setError('Content must be at least 50 characters');
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      
      const slug = generateSlug(formData?.title);
      
      // Check slug uniqueness
      const isUnique = await newsletterService?.isSlugUnique(slug);
      if (!isUnique) {
        setError('A newsletter with a similar title already exists. Please use a different title.');
        return;
      }

      await newsletterService?.create({
        ...formData,
        status: 'draft',
        slug
      });

      setSuccess('Newsletter saved as draft successfully!');
      setTimeout(() => {
        navigate('/admin-dashboard');
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Failed to save newsletter');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      
      const slug = generateSlug(formData?.title);
      
      // Check slug uniqueness
      const isUnique = await newsletterService?.isSlugUnique(slug);
      if (!isUnique) {
        setError('A newsletter with a similar title already exists. Please use a different title.');
        return;
      }

      await newsletterService?.create({
        ...formData,
        status: 'published',
        slug
      });

      setSuccess('Newsletter published successfully!');
      setTimeout(() => {
        navigate('/admin-dashboard');
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Failed to publish newsletter');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />
      
      <main className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin-dashboard')}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Create New Newsletter</h1>
            <p className="text-muted-foreground">Fill in the details below to create a new newsletter edition</p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-900">Success</h3>
                <p className="text-sm text-green-700 mt-1">{success}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="bg-card border border-border rounded-lg shadow-elevation-sm p-6 space-y-6">
            {/* Title Field */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Newsletter Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                placeholder="e.g., THEPS Newsletter - January 2025"
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                maxLength={200}
                disabled={loading}
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-muted-foreground">This will be the main headline of your newsletter</p>
                <span className="text-xs text-muted-foreground">{charCounts?.title}/200</span>
              </div>
            </div>

            {/* Excerpt Field */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Short Excerpt / Summary <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData?.excerpt}
                onChange={(e) => handleInputChange('excerpt', e?.target?.value)}
                placeholder="Write a brief summary that will be shown in previews and listings..."
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={4}
                maxLength={500}
                disabled={loading}
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-muted-foreground">A short description that appears on the home page and archive</p>
                <span className="text-xs text-muted-foreground">{charCounts?.excerpt}/500</span>
              </div>
            </div>

            {/* Content Field */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Full Content <span className="text-red-500">*</span>
              </label>
              
              <RichTextEditor
                id="newsletter-content"
                value={formData?.content}
                onChange={(value) => handleInputChange('content', value)}
                placeholder="Write your newsletter content here... Use the image button in the toolbar to upload images."
                disabled={loading}
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-muted-foreground">The complete newsletter content with all details</p>
                <span className="text-xs text-muted-foreground">{charCounts?.content} characters</span>
              </div>
            </div>

            {/* Word Count Stats */}
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Content Statistics</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Title Length</p>
                  <p className="text-lg font-semibold text-foreground">{charCounts?.title} chars</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Excerpt Length</p>
                  <p className="text-lg font-semibold text-foreground">{charCounts?.excerpt} chars</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Content Length</p>
                  <p className="text-lg font-semibold text-foreground">{charCounts?.content} chars</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <button
                onClick={() => navigate('/admin-dashboard')}
                className="px-6 py-3 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDraft}
                className="inline-flex items-center gap-2 px-6 py-3 bg-muted border border-border rounded-lg text-foreground hover:bg-muted/80 transition-colors"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>Save as Draft</span>
              </button>
              <button
                onClick={handlePublish}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>Publish Newsletter</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateNewsletterPage;